import { isURL } from 'validator';
import {
    isEmpty as _isEmpty,
    isString as _isString,
    isBoolean as _isBoolean,
    isObject as _isObject,
    isArray as _isArray,
} from 'lodash';

import { ERR_DYNAMIC_VALIDATE_SESSION_OPTIONAL, ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED } from '../../constants/Errors';

const validateSessionOptionalProps = (session) => {
    try {
        const { src, containerStyle, blocks, autoSave } = session;

        // src (optional)
        if (
            src !== undefined &&
            (!_isString(src) || !isURL(src, { protocols: ['http', 'https'], require_tld: false }))
        ) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_OPTIONAL('src'));
        }

        // containerStyle (optional)
        // TODO: optional, for now we are not checking if all of the properties passed are valid CSS ones, but this might be a future improvement
        if (containerStyle !== undefined && !_isObject(containerStyle)) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_OPTIONAL('containerStyle'));
        }

        // blocks (optional)
        // TODO: optional, for now we are not checking if the blocks are valid, but this might be a future improvement
        if (blocks !== undefined && !_isObject(blocks)) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_OPTIONAL('blocks'));
        }

        // autoSave (optional)
        if (autoSave !== undefined && !_isBoolean(autoSave)) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_OPTIONAL('autoSave'));
        }

        return null;
    } catch (err) {
        return err;
    }
};

const validateSession = (session) => {
    try {
        const { authorization, integration, controls, handlers, collaboration } = session;
        const sessionKeys = Object.keys(session);

        // authorization (required)
        if (!sessionKeys.includes('authorization') || !_isString(authorization) || _isEmpty(authorization)) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED('authorization'));
        }

        // integration (required)
        const validIntegrations = [
            'LOCALHOST',
            'APP',
            'GMAIL',
            'OUTLOOK_365',
            'OUTLOOK_CLASSIC_3.0',
            'OUTLOOK_CLASSIC',
        ];
        if (
            !sessionKeys.includes('integration') ||
            !_isString(integration) ||
            !validIntegrations.includes(integration)
        ) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED('integration'));
        }

        // controls (required)
        const validControlKeys = ['save', 'saveAsTemplate', 'saveBlock', 'download', 'sendTest', 'shareDraft'];
        if (
            !sessionKeys.includes('controls') ||
            !_isArray(controls) ||
            !controls.every((key) => validControlKeys.includes(key))
        ) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED('controls'));
        }

        // handlers (required)
        // session handlers reach this point as an empty object, we will have to validate it in bt-pikasso instead
        const validHandlerKeys = [
            'onMount',
            'onNotification',
            'onError',
            'onDownload',
            'onSendTest',
            'onSave',
            'onSaveAsTemplate',
        ];
        if (
            !sessionKeys.includes('handlers') ||
            !_isObject(handlers) ||
            !Object.keys(handlers).every((key) => validHandlerKeys.includes(key))
        ) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED('handlers'));
        }

        // TODO: add check for hasStaffbaseAccount, will have to add to all integrations to pass this prop to make it "required" instead of "optional"
        // collaboration (required)
        const validCollaborationKeys = ['domain', 'id', 'trackID', 'versionID', 'type', 'access'];
        const validDocumentTypes = ['draft', 'template', 'scratch', 'prebuilt'];
        if (
            !sessionKeys.includes('collaboration') ||
            !_isObject(collaboration) ||
            !validCollaborationKeys.every(
                (key) => Object.keys(collaboration).includes(key) && _isString(collaboration[key])
            ) ||
            !validDocumentTypes.includes(collaboration.type.toLowerCase()) ||
            collaboration.id.length <= 0 ||
            collaboration.trackID.length <= 0 ||
            collaboration.versionID.length <= 0
        ) {
            return new Error(ERR_DYNAMIC_VALIDATE_SESSION_REQUIRED('collaboration'));
        }

        return validateSessionOptionalProps(session);
    } catch (err) {
        return err;
    }
};

export default validateSession;
